import { useState } from 'react'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { useAuth } from '../../auth/useAuth'
import { useProfileState, type Profile as ProfileRecord } from '../../auth/useProfile'
import type { SignInProvider } from '../../auth/port'
import { Async } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { useToast } from '../../platform/feedback/ToastProvider'
import { Button } from '../../platform/primitives/Button'
import { Field } from '../../platform/primitives/Field'
import { PersonAvatar } from '../../platform/primitives/PersonAvatar'
import { kindName } from './Security'
import { useSetDisplayName } from './useSetDisplayName'
import './profile.css'

const PROVIDER_LABEL: Record<SignInProvider, string> = {
  password: 'Email and password',
  google: 'Google',
}

/**
 * Settings › Profile — M13. Who you are, as TrackE5 and your sign-in provider
 * know it. Only the display name is editable: email and phone belong to the
 * identity provider, and there is no photo upload.
 *
 * **Starts at `<h2>`**: `ChromeLayout` owns the document's only `<h1>`.
 */
export function Profile() {
  const state = useProfileState(
    (p) => p,
    'We couldn’t load your profile. Check your connection and try again.',
  )
  return (
    <section className="profile" aria-labelledby="profile-title">
      <h2 className="profile__title" id="profile-title">
        Profile
      </h2>
      <Async state={state} surface="profile" skeleton={<Skeleton height={160} />} empty={{ title: 'No profile' }}>
        {(profile) => <ProfileBody profile={profile} />}
      </Async>
    </section>
  )
}

function ProfileBody({ profile }: { profile: ProfileRecord }) {
  const auth = useAuth()
  const { show } = useToast()
  const set = useSetDisplayName()
  const [draft, setDraft] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  // The provider photo failing to load falls back to initials. Remembered per
  // URL, so a new photo gets its own chance.
  const [brokenPhoto, setBrokenPhoto] = useState<string | null>(null)

  const saved = profile.display_name ?? ''
  const value = draft ?? saved
  const trimmed = value.trim()
  const unchanged = trimmed === saved.trim()

  // Read once: both come from Firebase's live user object, and Security.tsx
  // records why a render-body read of that is fragile.
  const [{ providers, unrecognised, phoneNumber }] = useState(() => auth.identity())
  const [kinds] = useState(() => [...new Set(auth.enrolledFactors().map((f) => f.kind))])

  const memberSince = new Date(profile.created_at).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  })
  const photo = profile.photo_url ?? null

  const submit = () => {
    if (unchanged || set.isPending) return
    setFailed(false)
    set.mutate(trimmed === '' ? null : trimmed, {
      onSuccess: () => {
        setDraft(null)
        show({ kind: 'success', message: 'Name saved.' })
      },
      onError: () => setFailed(true),
    })
  }

  return (
    <div className="profile__body">
      <div className="profile__identity">
        {photo !== null && brokenPhoto !== photo ? (
          // The provider's own photo, copied by the backend from the Firebase
          // `picture` claim. It loads from the provider's image host, which no
          // CSP restricts yet — when #51 lands, that host joins img-src.
          <img
            className="profile__photo"
            src={photo}
            alt=""
            referrerPolicy="no-referrer"
            onError={() => setBrokenPhoto(photo)}
          />
        ) : (
          <PersonAvatar name={profile.display_name ?? null} email={profile.email} />
        )}
        <span className="profile__since">Member since {memberSince}</span>
      </div>

      <form
        className="profile__row"
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
      >
        <Field
          label="Display name"
          value={value}
          maxLength={255}
          autoComplete="name"
          onChange={(e) => {
            setDraft(e.target.value)
            setFailed(false)
          }}
          hint={trimmed === '' ? 'Leave empty to show your email' : undefined}
          error={failed ? 'We couldn’t save your name. Check your connection and try again.' : undefined}
        />
        <Button
          type="submit"
          variant="primary"
          aria-disabled={unchanged || set.isPending ? true : undefined}
          aria-describedby="profile-save-reason"
        >
          {set.isPending ? 'Saving…' : 'Save'}
        </Button>
        <span className="profile__reason" id="profile-save-reason">
          {unchanged && !set.isPending ? 'Change the name to save.' : ''}
        </span>
      </form>

      <dl className="profile__facts">
        <div className="profile__row">
          <dt>Email</dt>
          <dd>
            {profile.email}
            <span className="profile__note">Email and phone number can’t be changed here.</span>
          </dd>
        </div>
        {phoneNumber !== null && (
          <div className="profile__row">
            <dt>Phone</dt>
            <dd>{phoneNumber}</dd>
          </div>
        )}
        <div className="profile__row">
          <dt>Two-step verification</dt>
          <dd>
            {kinds.length === 0 ? 'Off' : `On · ${kinds.map(kindName).join(', ')}`}{' '}
            <InlineLink className="profile__link" to="/settings/security">Manage in Security</InlineLink>
          </dd>
        </div>
        <div className="profile__row">
          <dt>Sign-in method</dt>
          <dd>
            {providers.length > 0
              ? providers.map((p) => PROVIDER_LABEL[p]).join(' · ')
              : unrecognised.length > 0
                ? 'Another sign-in method'
                : ''}
          </dd>
        </div>
      </dl>
    </div>
  )
}
